import { useMemo } from "react";
import {
  createSearchParams,
  generatePath,
  Link,
  matchPath,
  Outlet,
  useMatches,
  useOutletContext,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { pathname } from "#src/config/pathname";
import useFetch from "#src/hooks/useFetch";
import paymentService from "#src/services/payment";
import { BillingOverviewCard } from "#src/components/workspace/billing/OverviewCard";
import {
  PaymentStatusValue,
  WorkspaceMemberRoleValue,
} from "#src/config/filter/workspace/value";
import { useAppSelector } from "#src/store/hooks";
import { BreadcrumbV2 } from "#src/commonV2/Breadcrumb";
import { BookOpen01 } from "@untitled-ui/icons-react";
import { ContractStatusValue } from "#src/components/workspace/billing/contract/enum";
import { SolutionStatusValue } from "#src/services/payment/enum";
import { BillingSolutionSearchParamKey } from "./solution";
import { BillingContractSearchParamKey } from "./contract";
import { BillingOrderSearchParamKey } from "./order";
import { LoadingStateV2 } from "#src/commonV2/LoadingState";

export enum BillingTabEnum {
  SOLUTION,
  ORDER,
  CONTRACT,
  CONFIG,
}

const WorkspaceBillingPage = () => {
  const { t } = useTranslation("workspace", { keyPrefix: "billing" });

  const { workspaceId } = useOutletContext<{
    workspaceId: string;
  }>();
  const pathMatches = useMatches();

  const currentWorkspace = useAppSelector(
    (state) => state.auth.currentWorkspace
  );

  // --------------- INITIALIZATION ---------------

  const tabList: { key: BillingTabEnum; path: string; label: string }[] = [
    {
      key: BillingTabEnum.SOLUTION,
      path: pathname.WORKSPACE_BILLING_SOLUTIONS,
      label: t("tab.solution.label"),
    },
    {
      key: BillingTabEnum.ORDER,
      path: pathname.WORKSPACE_BILLING_ORDERS,
      label: t("tab.order.label"),
    },
    {
      key: BillingTabEnum.CONTRACT,
      path: pathname.WORKSPACE_BILLING_CONTRACTS,
      label: t("tab.contract.label"),
    },
    {
      key: BillingTabEnum.CONFIG,
      path: pathname.WORKSPACE_BILLING_SETTINGS,
      label: t("tab.config.label"),
    },
  ];

  // --------------- HOOKS ---------------

  const fetchOverviewParams = useMemo<
    Parameters<typeof paymentService.retrieveWorkspacePaymentOverview>
  >(() => [workspaceId ?? ""], [workspaceId]);

  const { result: paymentOverview } = useFetch(
    paymentService.retrieveWorkspacePaymentOverview,
    fetchOverviewParams
  );

  // --------------- RENDER ---------------

  return currentWorkspace === undefined ? (
    <LoadingStateV2 />
  ) : [WorkspaceMemberRoleValue.OWNER, WorkspaceMemberRoleValue.ADMIN].includes(
      currentWorkspace.role
    ) ? (
    <>
      <div className="flex flex-col pt-5 pb-8 gap-5 overflow-x-hidden overflow-y-auto h-full">
        {/* Header (Breadcrumb + title) */}
        <div className="px-8 flex flex-col gap-4">
          <BreadcrumbV2
            levels={[
              {
                name: t("breadcrumb.levels.workspaceSettings"),
                path: generatePath(pathname.ENDPOINT_OVERVIEW, {
                  workspaceId,
                }),
              },
            ]}
            currentPage={{
              name: t("breadcrumb.current.billing"),
            }}
          />
          <div className="flex flex-col gap-1">
            <span className="text-display-xs font-semibold text-gray-v2-900">
              {t("title.parent")} <span className="text-gray-v2-300">/</span>{" "}
              {t("title.current")}
            </span>
            <div className="flex items-center gap-1.5 text-brand-v2-700">
              <BookOpen01 className="h-5 w-5 min-w-5" />
              <Link to="." className="text-md font-semibold no-underline">
                {t("understandOurPolicies")}
              </Link>
            </div>
          </div>
        </div>
        {/* Header (Breadcrumb + title) end */}

        {/* Overview */}
        <div className="px-8 flex flex-col gap-5">
          <div className="grid grid-cols-3 gap-4">
            <BillingOverviewCard
              title={t("overviewCard.renewService.title", {
                count: paymentOverview?.expiring_plans ?? 0,
              })}
              hint={t("overviewCard.renewService.hint")}
              linkTo={
                paymentOverview && paymentOverview.expiring_plans
                  ? {
                      pathname: generatePath(
                        pathname.WORKSPACE_BILLING_SOLUTIONS,
                        {
                          workspaceId,
                        }
                      ),
                      search: createSearchParams({
                        [BillingSolutionSearchParamKey.STATUS]:
                          SolutionStatusValue.EXPIRING,
                      }).toString(),
                    }
                  : null
              }
              value={paymentOverview ? paymentOverview.expiring_plans : null}
              bgTwColor={"bg-error-v2-100"}
              markerTwColor={"bg-error-v2"}
            />
            <BillingOverviewCard
              title={t("overviewCard.paymentNeeded.title", {
                count: paymentOverview?.pending_payments ?? 0,
              })}
              hint={t("overviewCard.paymentNeeded.hint")}
              linkTo={
                paymentOverview && paymentOverview.pending_payments
                  ? {
                      pathname: generatePath(
                        pathname.WORKSPACE_BILLING_ORDERS,
                        {
                          workspaceId,
                        }
                      ),
                      search: createSearchParams({
                        [BillingOrderSearchParamKey.STATUS]:
                          PaymentStatusValue.PROCESSING,
                      }).toString(),
                    }
                  : null
              }
              value={paymentOverview ? paymentOverview.pending_payments : null}
              bgTwColor={"bg-purple-v2-100"}
              markerTwColor={"bg-purple-v2"}
            />
            <BillingOverviewCard
              title={t("overviewCard.signingNeeded.title", {
                count: paymentOverview?.pending_contracts ?? 0,
              })}
              hint={t("overviewCard.signingNeeded.hint")}
              linkTo={
                paymentOverview && paymentOverview.pending_contracts
                  ? {
                      pathname: generatePath(
                        pathname.WORKSPACE_BILLING_CONTRACTS,
                        {
                          workspaceId,
                        }
                      ),
                      search: createSearchParams({
                        [BillingContractSearchParamKey.STATUS]:
                          ContractStatusValue.NOT_SIGNED,
                      }).toString(),
                    }
                  : null
              }
              value={paymentOverview ? paymentOverview.pending_contracts : null}
              bgTwColor={"bg-success-v2-100"}
              markerTwColor={"bg-success-v2"}
            />
          </div>
        </div>
        {/* Overview end */}

        {/* Tabs */}
        <div className="px-8 flex flex-col gap-5">
          <div className="flex gap-3 border-b border-gray-v2-200">
            {tabList.map((tab) => (
              <Link
                key={tab.key}
                to={generatePath(tab.path, { workspaceId })}
                className={`pb-3 px-6 text-sm font-semibold border-b-2 transition-colors no-underline ${
                  pathMatches.some((match) =>
                    matchPath(tab.path, match.pathname)
                  )
                    ? "text-brand-v2-700 border-brand-v2-600"
                    : "text-gray-v2 border-transparent"
                }`}
              >
                {tab.label}
              </Link>
            ))}
          </div>

          <Outlet context={{ workspaceId }} />
        </div>
        {/* Tabs end */}
      </div>
    </>
  ) : null;
};

export default WorkspaceBillingPage;
